import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  onValue,
  push,
  set,
  remove,
  update,
  get
} from 'firebase/database';
import './index.css';

// Configuration Firebase depuis .env
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialiser Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const database = getDatabase(app);

// Email de l'administrateur
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'rmanfirst20@gmail.com';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProjects: 0,
    totalTutorials: 0,
    totalGroups: 0
  });

  // Authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email === ADMIN_EMAIL) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Charger les données
  useEffect(() => {
    if (!user) return;

    // Charger les utilisateurs
    const usersRef = ref(database, 'users');
    const unsubscribeUsers = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const usersList = Object.entries(data).map(([id, user]) => ({ id, ...user }));
        setUsers(usersList);
        setStats(prev => ({ ...prev, totalUsers: usersList.length }));
      }
    });

    // Charger les projets
    const projectsRef = ref(database, 'projects');
    const unsubscribeProjects = onValue(projectsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const projectsList = Object.entries(data).map(([id, project]) => ({ id, ...project }));
        setProjects(projectsList);
        setStats(prev => ({ ...prev, totalProjects: projectsList.length }));
      }
    });

    // Charger les tutoriels
    const tutorialsRef = ref(database, 'tutorials');
    const unsubscribeTutorials = onValue(tutorialsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const tutorialsList = Object.entries(data).map(([id, tutorial]) => ({ id, ...tutorial }));
        setTutorials(tutorialsList);
        setStats(prev => ({ ...prev, totalTutorials: tutorialsList.length }));
      }
    });

    // Charger les groupes
    const groupsRef = ref(database, 'groups');
    const unsubscribeGroups = onValue(groupsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const groupsList = Object.entries(data).map(([id, group]) => ({ id, ...group }));
        setGroups(groupsList);
        setStats(prev => ({ ...prev, totalGroups: groupsList.length }));
      }
    });

    return () => {
      unsubscribeUsers();
      unsubscribeProjects();
      unsubscribeTutorials();
      unsubscribeGroups();
    };
  }, [user]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <LoginScreen setUser={setUser} />;
  }

  return (
    <div className="admin-app">
      <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <div className="main-content">
        <Header user={user} />
        {currentPage === 'dashboard' && <Dashboard stats={stats} />}
        {currentPage === 'users' && <UsersManagement users={users} />}
        {currentPage === 'projects' && <ProjectsManagement projects={projects} users={users} />}
        {currentPage === 'tutorials' && <TutorialsManagement tutorials={tutorials} />}
        {currentPage === 'groups' && <GroupsManagement groups={groups} users={users} />}
        {currentPage === 'notifications' && <NotificationsManagement users={users} />}
      </div>
    </div>
  );
}

// Écran de chargement
function LoadingScreen() {
  return (
    <div className="loading-screen">
      <img src="/git.png" alt="Gitorg" className="loading-logo" />
      <div className="spinner"></div>
      <p>Chargement...</p>
    </div>
  );
}

// Écran de connexion
function LoginScreen({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (email !== ADMIN_EMAIL) {
      setError('Accès réservé aux administrateurs uniquement');
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Email ou mot de passe incorrect');
    }
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <img src="/git.png" alt="Gitorg" className="login-logo" />
        <h1>Administration Gitorg</h1>
        <p className="login-subtitle">Panneau d'administration</p>
        
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="email"
            placeholder="Email administrateur"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="btn-primary">Se connecter</button>
        </form>

        <div className="login-footer">
          <img src="/adnimi.png" alt="Adnimi" />
          <span>×</span>
          <img src="/z.png" alt="ZTech" />
        </div>
      </div>
    </div>
  );
}

// Sidebar
function Sidebar({ currentPage, setCurrentPage }) {
  const menuItems = [
    { id: 'dashboard', icon: '📊', label: 'Tableau de bord' },
    { id: 'users', icon: '👥', label: 'Utilisateurs' },
    { id: 'projects', icon: '📁', label: 'Projets' },
    { id: 'tutorials', icon: '🎓', label: 'Tutoriels' },
    { id: 'groups', icon: '👨‍👩‍👧‍👦', label: 'Groupes' },
    { id: 'notifications', icon: '🔔', label: 'Notifications' }
  ];

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <img src="/git.png" alt="Gitorg" />
        <h2>Gitorg Admin</h2>
      </div>
      <nav className="sidebar-nav">
        {menuItems.map(item => (
          <button
            key={item.id}
            className={`sidebar-item ${currentPage === item.id ? 'active' : ''}`}
            onClick={() => setCurrentPage(item.id)}
          >
            <span className="sidebar-icon">{item.icon}</span>
            <span className="sidebar-label">{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <button 
          className="btn-logout"
          onClick={() => signOut(auth)}
        >
          🚪 Déconnexion
        </button>
      </div>
    </div>
  );
}

// Header
function Header({ user }) {
  return (
    <div className="header">
      <h1>Panneau d'Administration</h1>
      <div className="header-user">
        <span>{user.email}</span>
        <div className="admin-badge">Admin</div>
      </div>
    </div>
  );
}

// Dashboard
function Dashboard({ stats }) {
  return (
    <div className="dashboard">
      <h2>Tableau de bord</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{stats.totalUsers}</h3>
            <p>Utilisateurs</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📁</div>
          <div className="stat-content">
            <h3>{stats.totalProjects}</h3>
            <p>Projets</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎓</div>
          <div className="stat-content">
            <h3>{stats.totalTutorials}</h3>
            <p>Tutoriels</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👨‍👩‍👧‍👦</div>
          <div className="stat-content">
            <h3>{stats.totalGroups}</h3>
            <p>Groupes</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Gestion des utilisateurs
function UsersManagement({ users }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyUserId, setNotifyUserId] = useState(null);

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const deleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action supprimera également tous ses projets et données associées.')) return;

    try {
      // Supprimer l'utilisateur
      await remove(ref(database, `users/${userId}`));
      
      // Supprimer les projets de l'utilisateur
      const projectsSnapshot = await get(ref(database, 'projects'));
      if (projectsSnapshot.exists()) {
        const projectsData = projectsSnapshot.val();
        Object.entries(projectsData).forEach(async ([projectId, project]) => {
          if (project.userId === userId) {
            await remove(ref(database, `projects/${projectId}`));
          }
        });
      }

      alert('Utilisateur supprimé avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const openNotifyModal = (userId) => {
    setNotifyUserId(userId);
    setShowNotifyModal(true);
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>👥 Gestion des utilisateurs</h2>
        <input
          type="text"
          placeholder="🔍 Rechercher un utilisateur..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Projets</th>
              <th>Amis</th>
              <th>Inscription</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-cell">
                    <img src={user.photoURL || 'https://via.placeholder.com/40'} alt="" />
                    <span>{user.fullName || 'N/A'}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>{user.projectsCount || 0}</td>
                <td>{user.friends ? Object.keys(user.friends).length : 0}</td>
                <td>{new Date(user.createdAt).toLocaleDateString('fr-FR')}</td>
                <td>
                  <button
                    className="btn-action view"
                    onClick={() => setSelectedUser(user)}
                    title="Voir détails"
                  >
                    👁️
                  </button>
                  <button
                    className="btn-action notify"
                    onClick={() => openNotifyModal(user.id)}
                    title="Notifier"
                  >
                    🔔
                  </button>
                  <button
                    className="btn-action delete"
                    onClick={() => deleteUser(user.id)}
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}

      {showNotifyModal && (
        <NotifyUserModal 
          userId={notifyUserId} 
          onClose={() => {
            setShowNotifyModal(false);
            setNotifyUserId(null);
          }} 
        />
      )}
    </div>
  );
}

// Modal détails utilisateur
function UserDetailModal({ user, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>Détails de l'utilisateur</h2>
        <div className="user-details">
          <img src={user.photoURL || 'https://via.placeholder.com/120'} alt="" className="user-avatar" />
          <div className="user-info">
            <p><strong>Nom:</strong> {user.fullName || 'N/A'}</p>
            <p><strong>Email:</strong> {user.email}</p>
            <p><strong>Bio:</strong> {user.bio || 'Aucune bio'}</p>
            <p><strong>Localisation:</strong> {user.location || 'N/A'}</p>
            <p><strong>Date de naissance:</strong> {user.birthDate || 'N/A'}</p>
            <p><strong>Projets:</strong> {user.projectsCount || 0}</p>
            <p><strong>Amis:</strong> {user.friends ? Object.keys(user.friends).length : 0}</p>
            <p><strong>Vues du profil:</strong> {user.profileViews || 0}</p>
            <p><strong>Inscription:</strong> {new Date(user.createdAt).toLocaleString('fr-FR')}</p>
            <p><strong>Dernière connexion:</strong> {new Date(user.lastSeen).toLocaleString('fr-FR')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal notification utilisateur
function NotifyUserModal({ userId, onClose }) {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 'normal'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const notificationRef = push(ref(database, `notifications/${userId}`));
      await set(notificationRef, {
        ...formData,
        createdAt: Date.now(),
        read: false,
        fromAdmin: true
      });

      alert('Notification envoyée avec succès');
      onClose();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'envoi de la notification');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>🔔 Notifier l'utilisateur</h2>
        <form onSubmit={handleSubmit} className="notification-form">
          <input
            type="text"
            placeholder="Titre de la notification"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <textarea
            placeholder="Message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            required
            rows={4}
          />
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          >
            <option value="info">ℹ️ Information</option>
            <option value="warning">⚠️ Avertissement</option>
            <option value="success">✅ Succès</option>
            <option value="error">❌ Erreur</option>
          </select>
          <select
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
          >
            <option value="low">Faible priorité</option>
            <option value="normal">Priorité normale</option>
            <option value="high">Haute priorité</option>
          </select>
          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
            <button type="submit" className="btn-primary">Envoyer</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Gestion des projets
function ProjectsManagement({ projects, users }) {
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    userEmail: '',
    isPublic: true,
    tags: ''
  });

  const filteredProjects = projects.filter(project =>
    project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.userEmail?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = users.find(u => u.email === formData.userEmail);
    if (!user) {
      alert('Utilisateur non trouvé');
      return;
    }

    try {
      const projectRef = push(ref(database, 'projects'));
      await set(projectRef, {
        name: formData.name,
        description: formData.description,
        isPublic: formData.isPublic,
        userId: user.id,
        userEmail: user.email,
        userName: user.fullName || user.email,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        views: 0,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        comments: {},
        likes: {},
        shareLink: `${window.location.origin}/project/${projectRef.key}`
      });

      // Incrémenter le compteur
      await update(ref(database, `users/${user.id}`), {
        projectsCount: (user.projectsCount || 0) + 1
      });

      setFormData({ name: '', description: '', userEmail: '', isPublic: true, tags: '' });
      setShowForm(false);
      alert('Projet créé avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création');
    }
  };

  const deleteProject = async (projectId, userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) return;

    try {
      await remove(ref(database, `projects/${projectId}`));
      
      const user = users.find(u => u.id === userId);
      if (user) {
        await update(ref(database, `users/${userId}`), {
          projectsCount: Math.max((user.projectsCount || 0) - 1, 0)
        });
      }

      alert('Projet supprimé avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>📁 Gestion des projets</h2>
        <div className="header-actions">
          <input
            type="text"
            placeholder="🔍 Rechercher un projet..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Annuler' : '+ Nouveau projet'}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="form-card">
          <h3>Créer un nouveau projet</h3>
          <input
            type="text"
            placeholder="Nom du projet"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={4}
          />
          <input
            type="email"
            placeholder="Email de l'utilisateur"
            value={formData.userEmail}
            onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Tags (séparés par des virgules)"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.isPublic}
              onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
            />
            <span>Projet public</span>
          </label>
          <button type="submit" className="btn-primary">Créer le projet</button>
        </form>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Créateur</th>
              <th>Visibilité</th>
              <th>Vues</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.map(project => (
              <tr key={project.id}>
                <td><strong>{project.name}</strong></td>
                <td>{project.userEmail}</td>
                <td>
                  <span className={`badge ${project.isPublic ? 'public' : 'private'}`}>
                    {project.isPublic ? '🌍 Public' : '🔒 Privé'}
                  </span>
                </td>
                <td>{project.views || 0}</td>
                <td>{new Date(project.createdAt).toLocaleDateString('fr-FR')}</td>
                <td>
                  <button
                    className="btn-action delete"
                    onClick={() => deleteProject(project.id, project.userId)}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Gestion des tutoriels
function TutorialsManagement({ tutorials }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtubeUrl: '',
    language: '',
    level: 'beginner'
  });

  const extractYoutubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const videoId = extractYoutubeId(formData.youtubeUrl);
    if (!videoId) {
      alert('URL YouTube invalide');
      return;
    }

    try {
      const tutorialRef = push(ref(database, 'tutorials'));
      await set(tutorialRef, {
        ...formData,
        videoId,
        userId: auth.currentUser.uid,
        createdAt: Date.now(),
        likes: 0,
        comments: {}
      });

      setFormData({ title: '', description: '', youtubeUrl: '', language: '', level: 'beginner' });
      setShowForm(false);
      alert('Tutoriel créé avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création');
    }
  };

  const deleteTutorial = async (tutorialId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tutoriel ?')) return;

    try {
      await remove(ref(database, `tutorials/${tutorialId}`));
      alert('Tutoriel supprimé avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>🎓 Gestion des tutoriels</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Annuler' : '+ Nouveau tutoriel'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="form-card">
          <h3>Créer un nouveau tutoriel</h3>
          <input
            type="text"
            placeholder="Titre du tutoriel"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={3}
          />
          <input
            type="url"
            placeholder="Lien YouTube"
            value={formData.youtubeUrl}
            onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Langage de programmation"
            value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            required
          />
          <select
            value={formData.level}
            onChange={(e) => setFormData({ ...formData, level: e.target.value })}
          >
            <option value="beginner">Débutant</option>
            <option value="easy">Facile</option>
            <option value="intermediate">Intermédiaire</option>
            <option value="difficult">Difficile</option>
          </select>
          <button type="submit" className="btn-primary">Créer le tutoriel</button>
        </form>
      )}

      <div className="tutorials-grid">
        {tutorials.map(tutorial => (
          <div key={tutorial.id} className="tutorial-card">
            <img
              src={`https://img.youtube.com/vi/${tutorial.videoId}/hqdefault.jpg`}
              alt={tutorial.title}
            />
            <div className="tutorial-info">
              <h3>{tutorial.title}</h3>
              <p>{tutorial.language}</p>
              <span className="level-badge">{tutorial.level}</span>
              <button
                className="btn-action delete"
                onClick={() => deleteTutorial(tutorial.id)}
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Gestion des groupes (AMÉLIORÉ)
function GroupsManagement({ groups, users }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    creatorEmail: '',
    isPublic: true,
    tags: ''
  });

  const filteredGroups = groups.filter(group =>
    group.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const creator = users.find(u => u.email === formData.creatorEmail);
    if (!creator) {
      alert('Utilisateur créateur non trouvé');
      return;
    }

    try {
      if (editingGroup) {
        // Modifier un groupe existant
        await update(ref(database, `groups/${editingGroup.id}`), {
          name: formData.name,
          description: formData.description,
          isPublic: formData.isPublic,
          tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          updatedAt: Date.now()
        });
        alert('Groupe modifié avec succès');
      } else {
        // Créer un nouveau groupe
        const groupRef = push(ref(database, 'groups'));
        await set(groupRef, {
          name: formData.name,
          description: formData.description,
          isPublic: formData.isPublic,
          creatorId: creator.id,
          creatorEmail: creator.email,
          creatorName: creator.fullName || creator.email,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          members: {
            [creator.id]: {
              role: 'admin',
              joinedAt: Date.now()
            }
          },
          posts: {},
          settings: {
            allowMemberPosts: true,
            requireApproval: false
          }
        });
        alert('Groupe créé avec succès');
      }

      resetForm();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'opération');
    }
  };

  const editGroup = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      creatorEmail: group.creatorEmail,
      isPublic: group.isPublic,
      tags: group.tags ? group.tags.join(', ') : ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      creatorEmail: '',
      isPublic: true,
      tags: ''
    });
    setEditingGroup(null);
    setShowForm(false);
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe ? Tous les posts et membres seront supprimés.')) return;

    try {
      await remove(ref(database, `groups/${groupId}`));
      alert('Groupe supprimé avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>👨‍👩‍👧‍👦 Gestion des groupes</h2>
        <div className="header-actions">
          <input
            type="text"
            placeholder="🔍 Rechercher un groupe..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button 
            className="btn-primary" 
            onClick={() => {
              if (showForm && !editingGroup) {
                resetForm();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? 'Annuler' : '+ Nouveau groupe'}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="form-card">
          <h3>{editingGroup ? 'Modifier le groupe' : 'Créer un nouveau groupe'}</h3>
          <input
            type="text"
            placeholder="Nom du groupe"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <textarea
            placeholder="Description du groupe"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={4}
          />
          <input
            type="email"
            placeholder="Email du créateur"
            value={formData.creatorEmail}
            onChange={(e) => setFormData({ ...formData, creatorEmail: e.target.value })}
            required
            disabled={!!editingGroup}
          />
          <input
            type="text"
            placeholder="Tags (séparés par des virgules)"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.isPublic}
              onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
            />
            <span>Groupe public</span>
          </label>
          <div className="form-actions">
            <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            <button type="submit" className="btn-primary">
              {editingGroup ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Créateur</th>
              <th>Membres</th>
              <th>Visibilité</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map(group => (
              <tr key={group.id}>
                <td><strong>{group.name}</strong></td>
                <td>{group.creatorEmail}</td>
                <td>{group.members ? Object.keys(group.members).length : 0}</td>
                <td>
                  <span className={`badge ${group.isPublic ? 'public' : 'private'}`}>
                    {group.isPublic ? '🌍 Public' : '🔒 Privé'}
                  </span>
                </td>
                <td>{new Date(group.createdAt).toLocaleDateString('fr-FR')}</td>
                <td>
                  <button
                    className="btn-action edit"
                    onClick={() => editGroup(group)}
                    title="Modifier"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-action delete"
                    onClick={() => deleteGroup(group.id)}
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Gestion des notifications système
function NotificationsManagement({ users }) {
  const [formData, setFormData] = useState({
    target: 'all',
    selectedUsers: [],
    title: '',
    message: '',
    type: 'info',
    priority: 'normal',
    actionLink: '',
    expiresIn: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let targetUsers = [];

      if (formData.target === 'all') {
        targetUsers = users.map(u => u.id);
      } else if (formData.target === 'selected') {
        targetUsers = formData.selectedUsers;
      }

      if (targetUsers.length === 0) {
        alert('Veuillez sélectionner au moins un utilisateur');
        return;
      }

      const notification = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        priority: formData.priority,
        actionLink: formData.actionLink || null,
        createdAt: Date.now(),
        expiresAt: formData.expiresIn ? Date.now() + (parseInt(formData.expiresIn) * 24 * 60 * 60 * 1000) : null,
        read: false,
        fromAdmin: true
      };

      // Envoyer à tous les utilisateurs ciblés
      for (const userId of targetUsers) {
        const notificationRef = push(ref(database, `notifications/${userId}`));
        await set(notificationRef, notification);
      }

      alert(`Notification envoyée avec succès à ${targetUsers.length} utilisateur(s)`);
      
      // Réinitialiser le formulaire
      setFormData({
        target: 'all',
        selectedUsers: [],
        title: '',
        message: '',
        type: 'info',
        priority: 'normal',
        actionLink: '',
        expiresIn: ''
      });
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'envoi des notifications');
    }
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>🔔 Notifications système</h2>
        <p className="section-subtitle">Envoyer des notifications à tous les utilisateurs ou à une sélection</p>
      </div>

      <form onSubmit={handleSubmit} className="form-card notification-system-form">
        <h3>Créer une notification système</h3>

        <div className="form-group">
          <label>Destinataires</label>
          <select
            value={formData.target}
            onChange={(e) => setFormData({ ...formData, target: e.target.value })}
          >
            <option value="all">Tous les utilisateurs</option>
            <option value="selected">Utilisateurs sélectionnés</option>
          </select>
        </div>

        {formData.target === 'selected' && (
          <div className="form-group">
            <label>Sélectionner les utilisateurs</label>
            <div className="users-selection">
              {users.map(user => (
                <label key={user.id} className="checkbox-label user-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.selectedUsers.includes(user.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          selectedUsers: [...formData.selectedUsers, user.id]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          selectedUsers: formData.selectedUsers.filter(id => id !== user.id)
                        });
                      }
                    }}
                  />
                  <img src={user.photoURL || 'https://via.placeholder.com/30'} alt="" />
                  <span>{user.fullName || user.email}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Type de notification</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          >
            <option value="info">ℹ️ Information</option>
            <option value="warning">⚠️ Avertissement</option>
            <option value="success">✅ Succès</option>
            <option value="error">❌ Erreur</option>
            <option value="announcement">📢 Annonce</option>
            <option value="update">🔄 Mise à jour</option>
          </select>
        </div>

        <div className="form-group">
          <label>Priorité</label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
          >
            <option value="low">Faible</option>
            <option value="normal">Normale</option>
            <option value="high">Haute</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>

        <input
          type="text"
          placeholder="Titre de la notification"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />

        <textarea
          placeholder="Message détaillé"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          required
          rows={5}
        />

        <input
          type="text"
          placeholder="Lien d'action (optionnel)"
          value={formData.actionLink}
          onChange={(e) => setFormData({ ...formData, actionLink: e.target.value })}
        />

        <div className="form-group">
          <label>Expiration (en jours, optionnel)</label>
          <input
            type="number"
            placeholder="Nombre de jours avant expiration"
            value={formData.expiresIn}
            onChange={(e) => setFormData({ ...formData, expiresIn: e.target.value })}
            min="1"
          />
          <small>Laisser vide pour une notification permanente</small>
        </div>

        <div className="notification-preview">
          <h4>Aperçu de la notification</h4>
          <div className={`notification-card preview ${formData.type} ${formData.priority}`}>
            <div className="notification-icon">
              {formData.type === 'info' && 'ℹ️'}
              {formData.type === 'warning' && '⚠️'}
              {formData.type === 'success' && '✅'}
              {formData.type === 'error' && '❌'}
              {formData.type === 'announcement' && '📢'}
              {formData.type === 'update' && '🔄'}
            </div>
            <div className="notification-content">
              <h4>{formData.title || 'Titre de la notification'}</h4>
              <p>{formData.message || 'Votre message apparaîtra ici...'}</p>
              {formData.actionLink && (
                <button className="notification-action">Action</button>
              )}
            </div>
          </div>
        </div>

        <button type="submit" className="btn-primary btn-large">
          📤 Envoyer la notification
        </button>
      </form>
    </div>
  );
}

export default App;
